/**
 * Type definitions for Multi-Influencer Campaign System
 */

// Campaign Mode
export type CampaignMode = 'brand_action' | 'influencer_marketing' | 'sponsorship' | 'projecto' | 'services';

// Campaign Influencer Status (Pipeline Stages)
export type CampaignInfluencerStatus =
  | 'Prospecting'
  | 'Outreach'
  | 'Negotiating'
  | 'Accepted'
  | 'In Production'
  | 'Published'
  | 'Paid';

// Deliverable Status
export type DeliverableStatus =
  | 'Draft'
  | 'In Production'
  | 'Submitted'
  | 'Approved'
  | 'Published'
  | 'Rejected';

// Invoice Type
export type InvoiceType = 'Client' | 'Influencer';

// Invoice Status
export type InvoiceStatus =
  | 'Draft'
  | 'Issued'
  | 'Partially Paid'
  | 'Paid'
  | 'Overdue'
  | 'Canceled';

// ============================================================================
// Database Table Interfaces
// ============================================================================

export type CommissionType = 'percentage' | 'flat_fee';

export interface CampaignInfluencer {
  id: string;
  campaign_id: string;
  influencer_id: string | null;
  influencer_name: string;
  status: CampaignInfluencerStatus;
  agreed_fee: number | null;
  currency: string;
  commission_pct: number;
  commission_flat_fee: number;
  commission_type: CommissionType;
  usage_rights_json: Record<string, any> | null;
  agreement_url: string | null;
  notes: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface Deliverable {
  id: string;
  campaign_id: string;
  campaign_influencer_id: string | null;
  platform: string | null;
  type: string | null;
  title: string;
  description: string | null;
  due_date: string | null;
  status: DeliverableStatus;
  proof_url: string | null;
  published_at: string | null;
  published_url: string | null;
  is_overdue: boolean;
  fee: number | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceDeliverable {
  id: string;
  invoice_id: string;
  deliverable_id: string;
  fee_at_invoice: number;
  created_at: string;
}

export interface Invoice {
  id: string;
  campaign_id: string;
  campaign_influencer_id: string | null;
  type: InvoiceType;
  number: string | null;
  issue_date: string | null;
  due_date: string | null;
  subtotal: number;
  vat_rate: number;
  total: number;
  currency: string;
  status: InvoiceStatus;
  pdf_url: string | null;
  counterparty_name: string | null;
  counterparty_email: string | null;
  notes: string | null;
  is_overdue: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface Payment {
  id: string;
  invoice_id: string;
  amount: number;
  paid_at: string;
  method: string | null;
  reference: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface Brief {
  id: string;
  campaign_id: string;
  campaign_influencer_id: string | null;
  title: string;
  body_md: string | null;
  assets_url: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Extended/Computed Interfaces
// ============================================================================

export interface CampaignInfluencerWithStats extends CampaignInfluencer {
  deliverable_count: number;
  published_count: number;
  invoice_count: number;
  total_paid: number;
  net_fee_influencer: number; // agreed_fee - commission
  commission_amount: number;
}

export interface DeliverableWithInfluencer extends Deliverable {
  influencer_name?: string;
  days_until_due?: number | null;
}

export interface InvoiceWithPayments extends Invoice {
  payments: Payment[];
  total_paid: number;
  remaining: number;
  days_overdue?: number | null;
  latest_payment_date?: string | null;
}

export interface CampaignWithMultiInfluencer {
  id: string;
  campaign_mode: CampaignMode;
  // All existing campaign fields...
  influencers: CampaignInfluencerWithStats[];
  deliverables: DeliverableWithInfluencer[];
  client_invoices: InvoiceWithPayments[];
  influencer_invoices: InvoiceWithPayments[];
  briefs: Brief[];
  // Computed aggregates
  total_influencer_fees: number;
  total_commission: number;
  budget_margin: number; // client fee - total influencer fees
  deliverables_published_count: number;
  deliverables_total_count: number;
}

// ============================================================================
// Status Configuration (UI Colors and Display)
// ============================================================================

export const CAMPAIGN_MODE_CONFIG = {
  brand_action: {
    label: 'Ação Marca',
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    borderColor: 'border-blue-300',
  },
  influencer_marketing: {
    label: 'Marketing de Influência',
    color: 'purple',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-800',
    borderColor: 'border-purple-300',
  },
  sponsorship: {
    label: 'Patrocínio',
    color: 'teal',
    bgColor: 'bg-teal-100',
    textColor: 'text-teal-800',
    borderColor: 'border-teal-300',
  },
  services: {
    label: 'Serviços',
    color: 'slate',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-800',
    borderColor: 'border-slate-300',
  },
  projecto: {
    label: 'Projecto',
    color: 'amber',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-800',
    borderColor: 'border-amber-300',
  },
} as const;

export const CAMPAIGN_INFLUENCER_STATUS_CONFIG = {
  Prospecting: { color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800', order: 0 },
  Outreach: { color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800', order: 1 },
  Negotiating: { color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', order: 2 },
  Accepted: { color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800', order: 3 },
  'In Production': { color: 'indigo', bgColor: 'bg-indigo-100', textColor: 'text-indigo-800', order: 4 },
  Published: { color: 'teal', bgColor: 'bg-teal-100', textColor: 'text-teal-800', order: 5 },
  Paid: { color: 'emerald', bgColor: 'bg-emerald-100', textColor: 'text-emerald-800', order: 6 },
} as const;

export const DELIVERABLE_STATUS_CONFIG = {
  Draft: { color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800', order: 0 },
  'In Production': { color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800', order: 1 },
  Submitted: { color: 'indigo', bgColor: 'bg-indigo-100', textColor: 'text-indigo-800', order: 2 },
  Approved: { color: 'teal', bgColor: 'bg-teal-100', textColor: 'text-teal-800', order: 3 },
  Published: { color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800', order: 4 },
  Rejected: { color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800', order: -1 },
} as const;

export const INVOICE_STATUS_CONFIG = {
  Draft: { color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800' },
  Issued: { color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
  'Partially Paid': { color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
  Paid: { color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' },
  Overdue: { color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' },
  Canceled: { color: 'gray', bgColor: 'bg-gray-200', textColor: 'text-gray-600' },
} as const;

export const ENHANCED_CAMPAIGN_STATUS_CONFIG = {
  Consultation: { color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800', order: 0 },
  'In Progress': { color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800', order: 1 },
  'Active+Billing': { color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-800', order: 2 },
  Billing: { color: 'orange', bgColor: 'bg-orange-100', textColor: 'text-orange-800', order: 3 },
  Completed: { color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800', order: 4 },
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a campaign influencer status transition is valid
 */
export function canTransitionInfluencerStatus(
  from: CampaignInfluencerStatus,
  to: CampaignInfluencerStatus
): boolean {
  const fromOrder = CAMPAIGN_INFLUENCER_STATUS_CONFIG[from].order;
  const toOrder = CAMPAIGN_INFLUENCER_STATUS_CONFIG[to].order;
  // Allow forward progression and one step back
  return toOrder >= fromOrder - 1;
}

/**
 * Check if a deliverable status transition is valid
 */
export function canTransitionDeliverableStatus(
  from: DeliverableStatus,
  to: DeliverableStatus
): boolean {
  // Allow all transitions except from Published back to non-published
  if (from === 'Published' && to !== 'Published') {
    return false;
  }
  // Can always reject
  if (to === 'Rejected') {
    return true;
  }
  return true;
}

/**
 * Calculate net fee for influencer (after commission)
 * Supports both percentage and flat_fee commission types
 */
export function calculateNetFeeInfluencer(
  agreedFee: number,
  commissionPct: number,
  commissionFlatFee: number,
  commissionType: CommissionType = 'percentage'
): number {
  if (commissionType === 'flat_fee') {
    return agreedFee - (commissionFlatFee || 0);
  }
  return agreedFee * (1 - commissionPct);
}

/**
 * Calculate commission amount
 * Supports both percentage and flat_fee commission types
 */
export function calculateCommission(
  agreedFee: number,
  commissionPct: number,
  commissionFlatFee: number,
  commissionType: CommissionType = 'percentage'
): number {
  if (commissionType === 'flat_fee') {
    return commissionFlatFee || 0;
  }
  return agreedFee * commissionPct;
}

/**
 * Calculate budget margin (client budget - total influencer costs)
 */
export function calculateBudgetMargin(
  clientBudget: number,
  influencerFees: number[]
): number {
  const totalInfluencerCosts = influencerFees.reduce((sum, fee) => sum + fee, 0);
  return clientBudget - totalInfluencerCosts;
}

/**
 * Check if budget margin is at risk (negative or < 10%)
 */
export function isBudgetAtRisk(margin: number, clientBudget: number): boolean {
  if (margin < 0) return true;
  const marginPercentage = (margin / clientBudget) * 100;
  return marginPercentage < 10;
}

/**
 * Calculate sponsorship profit
 * Profit = Sponsorship Amount * Agency Commission Percent
 */
export function calculateSponsorshipProfit(
  sponsorshipAmount: number,
  agencyCommissionPercent: number
): number {
  return sponsorshipAmount * agencyCommissionPercent;
}

/**
 * Calculate days until due (negative if overdue)
 */
export function calculateDaysUntilDue(dueDate: string): number {
  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = due.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Check if item is due soon (within 3 days)
 */
export function isDueSoon(dueDate: string): boolean {
  const days = calculateDaysUntilDue(dueDate);
  return days >= 0 && days <= 3;
}

/**
 * Format date to pt-PT format (DD/MM/YYYY)
 */
export function formatDatePtPT(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format currency to pt-PT format (e.g., "17.000,20 €")
 */
export function formatCurrencyPtPT(amount: number): string {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}
